import Channel from '@/Enums/Channel.enum';
import { ChannelId } from '@/Enums/ChannelId.enum';
import ToggleButton from '@/components/Buttons/ToggleButton';
import DateRangeWithShortcuts from '@/components/DateRangeWithShortcuts';
import NumberInput from '@/components/NumberInput';
import InfoTooltip from '@/components/Tooltip/InfoTooltip';
import { calculateBidCpmGuidance, formatBidCpmGuidance } from '@/helpers/bidCpmGuidance';
import { getFieldErrorProps } from '@/helpers/form';
import useProductDomains from '@/hooks/useProductDomains';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectCampaignManagementProducts,
  selectChannels,
  selectLineItemChannel,
  selectVisibleLineItemChannels,
} from '@/store/selectors/campaignManagement.selectors';
import {
  selectAllowDeviceControl,
  selectIsProductSelectionCardHidden,
  selectShowBidCpmControl,
  selectShowBidCpmGuidance,
  selectShowRateCardCpmControl,
} from '@/store/selectors/currentWhitelabel.selectors';
import { getChannels } from '@/store/slices/campaignManagementSlice';
import type { LineItemChannel } from '@/types/LineItem/channel.types';
import TrackChanges from '@mui/icons-material/TrackChanges';
import { FormLabel, InputAdornment, TextField, Typography } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { Moment } from 'moment';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import DeviceList from './DeviceList';
import DeviceSection from './DeviceSection';
import { BID_CPM_MAX, BID_CPM_MIN, LINE_ITEM_CHANNEL_OPTIONS } from './constants';

interface IProps {
  index: number;
}

const BasicInfo = ({ index }: IProps) => {
  const activeLineItemChannel = useAppSelector(state => selectLineItemChannel(state, index));

  const showBidCpmControl = useAppSelector(selectShowBidCpmControl);
  const showBidCpmGuidance = useAppSelector(selectShowBidCpmGuidance);
  const showRateCardCpmControl = useAppSelector(selectShowRateCardCpmControl);
  const allowDeviceControl = useAppSelector(selectAllowDeviceControl);
  const channels = useAppSelector(selectChannels);
  const visibleLineItemChannels = useAppSelector(selectVisibleLineItemChannels);
  const isProductSelectionCardHidden = useAppSelector(selectIsProductSelectionCardHidden);
  const { products: productsWithPublishers } = useAppSelector(selectCampaignManagementProducts);

  const dispatch = useAppDispatch();

  const { control, setValue } = useFormContext();

  const [watchChannel, watchStartDate, watchEndDate, watchProduct, watchPublisherIds] = useWatch({
    control,
    name: ['channel', 'startDate', 'endDate', 'product', 'publisherIds', 'id'],
  });

  useEffect(() => {
    dispatch(getChannels());
  }, [dispatch]);

  const handleDatesChange = useCallback(
    ([startDate, endDate]: DateRange<Moment>) => {
      setValue('startDate', startDate || null, { shouldDirty: true, shouldValidate: true });
      setValue('endDate', endDate || null, { shouldDirty: true, shouldValidate: true });
    },
    [setValue],
  );

  const handleChannelChange = useCallback(
    (channelName: LineItemChannel) => () => {
      const match = channels?.find(c => c.name === channelName);
      setValue('channel', match ? { label: channelName, value: match.id as ChannelId } : null, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('product', null, { shouldDirty: true, shouldValidate: true });
      setValue('devices', [], { shouldDirty: true, shouldValidate: true });
      setValue('dealGroups', [], { shouldDirty: true, shouldValidate: true });
    },
    [setValue, channels],
  );

  const rateCardCpmValue = useMemo(() => watchProduct?.rateCardCpm ?? '', [watchProduct]);

  const selectedProduct = useMemo(() => {
    if (!showBidCpmGuidance || !watchProduct?.id) return null;
    const products = productsWithPublishers?.[watchChannel?.label as Channel]?.products ?? [];
    return products.find(product => product.id === watchProduct.id) ?? null;
  }, [showBidCpmGuidance, watchProduct?.id, productsWithPublishers, watchChannel?.label]);

  const guidanceDomainsProductId = useMemo(() => {
    const isDomainBacked = watchChannel?.label === Channel.Display || watchChannel?.label === Channel.OLV;
    if (!isDomainBacked || !selectedProduct || selectedProduct.publishers?.length) return null;
    return selectedProduct.id;
  }, [watchChannel?.label, selectedProduct]);

  const { domains: guidanceDomains } = useProductDomains(guidanceDomainsProductId);

  const bidCpmGuidance = useMemo(() => {
    if (!selectedProduct) return null;
    if (guidanceDomainsProductId) {
      return calculateBidCpmGuidance(guidanceDomains.map(domain => domain.bidCpmGuidance));
    }
    const selectedPublisherIds = new Set<number>(watchPublisherIds ?? []);
    return calculateBidCpmGuidance(
      (selectedProduct.publishers ?? [])
        .filter(publisher => selectedPublisherIds.has(publisher.id))
        .map(publisher => publisher.bidCpmGuidance),
    );
  }, [selectedProduct, guidanceDomainsProductId, guidanceDomains, watchPublisherIds]);

  const channelOptions = useMemo(
    () => LINE_ITEM_CHANNEL_OPTIONS.filter(option => visibleLineItemChannels.includes(option.value)),
    [visibleLineItemChannels],
  );

  /**
   * The channel toggles are hidden together with the product card, so a new line item keeps the
   * `defaultFormData` channel. Fall back to the first channel this org can actually use.
   */
  useEffect(() => {
    if (!isProductSelectionCardHidden || activeLineItemChannel || !channels?.length) return;
    if (!channelOptions.length || channelOptions.some(option => option.value === watchChannel?.label)) return;
    handleChannelChange(channelOptions[0].value)();
  }, [
    isProductSelectionCardHidden,
    activeLineItemChannel,
    channels?.length,
    channelOptions,
    watchChannel?.label,
    handleChannelChange,
  ]);

  return (
    <div className='flex flex-col w-full'>
      <div className='flex justify-between flex-row space-x-6'>
        <div className='flex flex-col w-full'>
          <FormLabel htmlFor={`line-item-name-${index}`} aria-label='Line Item Name' required>
            Line Item Name
          </FormLabel>
          <Controller
            name='name'
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                id={`line-item-name-${index}`}
                fullWidth
                placeholder='Enter Line Item Name'
                {...getFieldErrorProps(fieldState)}
              />
            )}
          />
        </div>
        <div className='flex flex-col w-full'>
          <FormLabel htmlFor={`impressions-booked-${index}`} aria-label='Impressions Booked' required>
            Impressions booked
          </FormLabel>
          <Controller
            name='impressionsBooked'
            control={control}
            render={({ field, fieldState }) => (
              <NumberInput
                {...field}
                // @ts-expect-error jsx component prop
                id={`impressions-booked-${index}`}
                fullWidth
                placeholder='Enter Impressions Booked'
                disableInputArrows
                NumberInputProps={{
                  thousandsSeparator: ',',
                  unmask: true,
                }}
                {...getFieldErrorProps(fieldState)}
              />
            )}
          />
        </div>
        <div className='flex flex-col w-full'>
          <FormLabel htmlFor={`date-range-${index}`} aria-label='Date Range' required>
            Date Range
          </FormLabel>
          <DateRangeWithShortcuts
            value={[watchStartDate, watchEndDate]}
            onChange={handleDatesChange}
            timezone='UTC'
            disablePast
            withTime
            inputProps={{
              InputProps: {
                id: `date-range-${index}`,
                placeholder: 'Select Date Range',
                startAdornment: null,
                sx: {
                  '& input': {
                    paddingRight: 0,
                  },
                  // Usually the date range would be counted as invalid on passed items bc of 'disablePast' setting
                  // We don't want to show the error border around the date range input
                  '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'inherit',
                  },
                },
              },
            }}
          />
        </div>
        <div className='flex flex-col w-full'>
          {/* Renamed from "Sold CPM" (#4446): this is the rate the client is
              billed at, and it now pairs with Client Spend in Unified
              Reporting. The form field, the API payload and the column stay
              `soldCpm` — only what the partner reads changes. */}
          <FormLabel htmlFor={`sold-cpm-${index}`} aria-label='Client CPM'>
            Client CPM
          </FormLabel>
          <Controller
            name='soldCpm'
            control={control}
            render={({ field, fieldState }) => (
              <NumberInput
                {...field}
                // @ts-expect-error jsx component prop
                id={`sold-cpm-${index}`}
                fullWidth
                placeholder='Enter Client CPM'
                disableInputArrows
                NumberInputProps={{
                  thousandsSeparator: ',',
                  unmask: true,
                }}
                {...getFieldErrorProps(fieldState)}
              />
            )}
          />
        </div>
        {showBidCpmControl && (
          <div className='flex flex-col w-full'>
            <FormLabel
              htmlFor={`bid-cpm-${index}`}
              aria-label='Bid CPM'
              className={bidCpmGuidance ? 'form-label-with-tooltip' : undefined}
              required
            >
              Bid CPM
              {bidCpmGuidance && (
                <InfoTooltip
                  title='Suggested bid range, based on the average CPM of the selected publishers.'
                  className='form-label-tooltip'
                />
              )}
            </FormLabel>
            <Controller
              name='bidCpm'
              control={control}
              render={({ field, fieldState }) => (
                <NumberInput
                  {...field}
                  // @ts-expect-error jsx component prop
                  id={`bid-cpm-${index}`}
                  fullWidth
                  placeholder='Enter Bid CPM'
                  disableInputArrows
                  min={BID_CPM_MIN}
                  max={BID_CPM_MAX}
                  maximumFractionDigits={2}
                  NumberInputProps={{ thousandsSeparator: ',', unmask: true }}
                  {...getFieldErrorProps(fieldState)}
                />
              )}
            />
            {bidCpmGuidance && (
              <Typography
                variant='caption'
                color='text.secondary'
                className='flex items-center gap-1 mt-1'
                aria-label='Bid CPM Guidance'
              >
                <TrackChanges sx={{ fontSize: 14 }} />
                Guidance: {formatBidCpmGuidance(bidCpmGuidance)}
              </Typography>
            )}
          </div>
        )}
        {showRateCardCpmControl && (
          <div className='flex flex-col w-full'>
            <FormLabel htmlFor={`rate-card-cpm-${index}`} aria-label='Rate Card CPM'>
              Rate Card CPM
            </FormLabel>
            <TextField
              id={`rate-card-cpm-${index}`}
              fullWidth
              value={rateCardCpmValue}
              disabled
              placeholder='Rate Card CPM'
              slotProps={{
                input: { readOnly: true, startAdornment: <InputAdornment position='start'>$</InputAdornment> },
              }}
            />
          </div>
        )}
        <div className='flex flex-col w-full'>
          <FormLabel htmlFor={`line-item-alt-id-${index}`} aria-label='External ID / Billing ID'>
            External ID / Billing ID
          </FormLabel>
          <Controller
            name='altId'
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                id={`line-item-alt-id-${index}`}
                fullWidth
                placeholder='Enter External ID / Billing ID'
                {...getFieldErrorProps(fieldState)}
              />
            )}
          />
        </div>
      </div>
      <div className='flex mt-3'>
        {!isProductSelectionCardHidden && (
          <div className='flex flex-1 flex-col'>
            <FormLabel aria-label='Channel'>Channel</FormLabel>
            <div className='flex flex-nowrap gap-x-2 gap-y-2 h-[32px]'>
              {channelOptions.map(({ label, value, Icon }) => (
                <ToggleButton
                  key={label}
                  label={label}
                  value={value}
                  startAdornment={<Icon />}
                  disabled={!!activeLineItemChannel}
                  selected={watchChannel?.label === value}
                  onClick={handleChannelChange(value)}
                />
              ))}
            </div>
          </div>
        )}
        {allowDeviceControl ? <DeviceSection /> : <DeviceList />}
      </div>
    </div>
  );
};

export default BasicInfo;
